import React, { useCallback, useEffect, useState } from "react";
import * as insightsActions from "src/redux/slices/insightsSlice";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import Shimmer from "src/components/common/Shimmer";
import moment from "moment";
import InfiniteScroll from "react-infinite-scroll-component";
import Loader from "src/components/common/Loader";
import {
  LucideBed,
  LucideDumbbell,
  LucideDroplets,
  LucideFootprints,
  LucideFlower,
  LucideMeh,
  LucidePill,
  LucideUtensils,
} from "lucide-react";
import {
  DATE_FORMAT_STRING,
  TIME_FORMAT_STRING,
} from "src/constants/dataTimeFormats";

interface LogHistoryProps {
  logDetailsScreenKey?: string | undefined;
}

type LogDetails = {
  type: string;
  name: string;
  icon: React.ReactNode;
};

const logger = (logDetailsScreenKey?: string): LogDetails => {
  const mapping: Record<string, LogDetails> = {
    "medication-logs": {
      type: "medication",
      name: "Medication Logs",
      icon: <LucidePill size={16} />,
    },
    "diet-logs": {
      type: "food",
      name: "Diet Logs",
      icon: <LucideUtensils size={16} />,
    },
    "water-logs": {
      type: "water",
      name: "Water Logs",
      icon: <LucideDroplets size={16} />,
    },
    "sleep-logs": {
      type: "sleep",
      name: "Sleep Logs",
      icon: <LucideBed size={16} />,
    },
    "steps-logs": {
      type: "steps",
      name: "Steps Logs",
      icon: <LucideFootprints size={16} />,
    },
    "exercise-logs": {
      type: "exercise",
      name: "Exercise Logs",
      icon: <LucideDumbbell size={16} />,
    },
    "fatigue-logs": {
      type: "fatigue",
      name: "Fatigue Logs",
      icon: <LucideMeh size={16} />,
    },
    "menstruation-logs": {
      type: "menstruation",
      name: "Menstruation Logs",
      icon: <LucideFlower size={16} />,
    },
  };

  return (
    mapping[logDetailsScreenKey || ""] || {
      type: "food",
      name: "Diet Logs",
      icon: <LucideUtensils size={16} />,
    }
  );
};

const LogHistory: React.FC<LogHistoryProps> = ({ logDetailsScreenKey }) => {
  const dispatch = useAppDispatch();
  const { patient } = useAppSelector((state) => state.auth);
  const { insightsHistory } = useAppSelector((state) => state.insights);

  const fetchLogHistory = useCallback(async () => {
    new Promise((resolve, reject) => {
      const params = {
        user_id: patient?.data?.patient_id,
        logger: logger(logDetailsScreenKey)?.type,
        page: 1,
      };
      dispatch(
        insightsActions.getHistoryRequest({
          queryParams: params,
          resolve,
          reject,
        })
      );
    });
  }, [dispatch, patient?.data?.patient_id, logDetailsScreenKey]);

  // Clear existing data when logDetailsScreenKey changes to avoid duplicate data
  useEffect(() => {
    dispatch(insightsActions.clearHistoryData());
    fetchLogHistory();
  }, [logDetailsScreenKey, fetchLogHistory]);

  const fetchMoreLogHistory = async () => {
    await new Promise((resolve, reject) => {
      const params = {
        user_id: patient?.data?.patient_id,
        logger: logger(logDetailsScreenKey)?.type,
        page: insightsHistory?.data?.page + 1,
      };
      dispatch(
        insightsActions.getHistoryRequest({
          queryParams: params,
          resolve,
          reject,
        })
      );
    });
  };

  return (
    <div className="p-4 relative">
      <div className="w-full space-y-2 fixed top-[130px] left-0 right-0 bg-white border-b border-gray-100 z-10 max-w-lg mx-auto">
        {/* <div className="flex items-center w-full h-10 border border-gray-200 rounded-md overflow-hidden focus-within:border-[var(--app-primary)]">
          <span className="flex items-center justify-center w-10 h-10">
            <Search className="w-4 h-4 text-gray-400" />
          </span>
          <input
            type="text"
            name=""
            id=""
            placeholder="Search water logs..."
            className="w-full h-full text-sm font-light"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div> */}

        {/* <Input
          name="search"
          type="text"
          placeholder="Search water logs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          startAdornment={<Search className="w-4 h-4 text-gray-400" />}
        />

        <div className="flex items-center gap-2 flex-wrap">
          {tempFilters.map((filter) => (
            <button
              type="button"
              key={filter.id}
              className={`text-xs font-light px-3 py-1.5 rounded-full bg-gray-100 border whitespace-nowrap ${
                activeFilter === filter.name
                  ? "bg-white text-[var(--app-primary)] border-[var(--app-primary)]"
                  : "bg-gray-100 border-gray-100"
              }`}
              onClick={() =>
                setActiveFilter(
                  activeFilter === filter.name ? null : filter.name
                )
              }
            >
              {filter.name}
            </button>
          ))}
        </div>
      </div>
      <div className="w-full pt-28">
        <div className="space-y-4">
          {/* {insightsHistory?.data?.map((item: any) => (
            <div className="w-full p-4 shadow-sm border-0 bg-white rounded-lg overflow-hidden">
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--icon-bg-primary)]">
                  <span className="text-lg text-[var(--icon-primary)]">
                    {item.icon}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium">hello</span>
                  <span className="text-xs font-light text-gray-500">
                    {item.date}
                  </span>
                </div>
              </div>
            </div>
          ))} 
        </div> */}
      </div>

      {insightsHistory?.loading && (
        <div className="space-y-4">
          <Shimmer width="100%" height="72px" />
          <Shimmer width="100%" height="72px" />
          <Shimmer width="100%" height="72px" />
          <Shimmer width="100%" height="72px" />
        </div>
      )}

      <div className="relative w-full">
        <InfiniteScroll
          dataLength={insightsHistory?.data?.history?.length || 0}
          next={fetchMoreLogHistory}
          hasMore={
            insightsHistory?.data?.page * insightsHistory?.data?.limit <
            insightsHistory?.data?.full_count
          }
          loader={
            <div className="flex items-center justify-center py-4">
              <Loader size="lg" />
            </div>
          }
          scrollableTarget="scrollableTarget"
        >
          <div className="space-y-4">
            {insightsHistory?.data?.history?.length > 0 ? (
              insightsHistory?.data?.history?.map(
                (item: any, index: number) => (
                  <LogHistoryItem
                    key={index}
                    item={item}
                    logDetailsScreenKey={logDetailsScreenKey}
                  />
                )
              )
            ) : (
              <>
                {!insightsHistory?.loading && (
                  <div className="text-center py-10">
                    <span className="text-sm font-medium">No data found</span>
                  </div>
                )}
              </>
            )}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default LogHistory;

const LogHistoryItem = ({
  item,
  logDetailsScreenKey,
}: {
  item: any;
  logDetailsScreenKey?: string;
}) => {
  const name =
    item.meal_name || item.exercise_name || item.value || item.medicine_name;
  const calories = item.calories || item.calories_burnt || null;
  const time = item.time || item.date;
  const unit = item.unit || "";
  return (
    <div className="w-full p-4 shadow-sm border-0 bg-white rounded-lg overflow-hidden">
      <div className="flex items-center gap-4">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--icon-bg-primary)]">
          <span className="text-md text-[var(--app-primary)]">
            {logger(logDetailsScreenKey)?.icon}
          </span>
        </div>
        <div className="w-full flex flex-col gap-1">
          <div className="w-full flex items-start justify-between gap-2">
            <span className="flex-1 text-sm font-medium block line-clamp-1">
              {name}{" "}
              {unit &&
                logDetailsScreenKey !== "exercise-logs" &&
                logDetailsScreenKey !== "diet-logs" && (
                  <span className="lowercase">{unit}</span>
                )}
            </span>
            {calories && (
              <span className="text-xs font-light text-gray-500 min-w-12 text-right">
                {calories || "0 kcal"}
              </span>
            )}
          </div>
          <div className="text-xs font-light text-gray-500 w-full">
            {moment.utc(time).format(DATE_FORMAT_STRING)} •{" "}
            {moment.utc(time).local().format(TIME_FORMAT_STRING)}
          </div>
        </div>
      </div>
    </div>
  );
};
